import { useEffect, useRef, useState } from "react";
import { Drawer, Popover } from "@mui/material";
import { toast } from "sonner";
import { toastApiError } from "@/api/client";
import {
  useHeartbeat,
  useMeSessionState,
  usePauseSession,
  useResumeSession,
  useStartSession,
  useStopSession,
  useSwitchSession,
  type SessionRow,
} from "@/api/hooks/timetracking";
import { useConfirm } from "@/shared/components/ConfirmDialog";
import { useChannel } from "@/modules/realtime";
import { useAuthStore } from "@/modules/auth";
import { useQueryClient } from "@tanstack/react-query";
import { WorkUnitPickerDialog } from "./WorkUnitPickerDialog";
import { PipTimerWindow, PIP_SUPPORTED } from "./PipTimerWindow";
import { PipTimerContent } from "./PipTimerContent";

const HEARTBEAT_INTERVAL_MS = 30_000;
const NETWORK_DOWN_STREAK = 3;

function fmt(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function PillBody({
  session,
  activeMs,
  pausedMs,
  onPause,
  onResume,
  onSwitch,
  onStop,
  onDetach,
  busy,
  hideDetach,
}: {
  session: SessionRow;
  activeMs: number;
  pausedMs: number;
  onPause: () => void;
  onResume: () => void;
  onSwitch: () => void;
  onStop: () => void;
  onDetach: () => void;
  busy?: boolean;
  hideDetach?: boolean;
}) {
  const isWorking = session.state === "working";
  const accent = isWorking ? "#059669" : "#b45309";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: 12,
        minWidth: 300,
        background: "var(--surface)",
        border: `1px solid var(--line)`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 8,
        boxShadow: "var(--shadow-md)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: accent,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: accent,
              animation: isWorking ? "wmBlink 1s infinite" : undefined,
            }}
          />
          {isWorking
            ? "Working"
            : `Paused${session.pauseReason ? ` · ${session.pauseReason.replace(/_/g, " ")}` : ""}`}
        </span>
        {!hideDetach && (
          <button
            type="button"
            onClick={onDetach}
            title="Detach to floating window"
            style={{
              border: "1px solid var(--line)",
              borderRadius: 4,
              background: "var(--surface)",
              color: "var(--ink-3)",
              fontSize: 11,
              cursor: "pointer",
              padding: "2px 8px",
            }}
          >
            ⤢
          </button>
        )}
      </div>

      {/* Active + Paused timers */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <TimerStat
          label="Active"
          value={fmt(activeMs)}
          live={isWorking}
          color={isWorking ? "#059669" : "var(--ink-2)"}
        />
        <TimerStat
          label="Paused"
          value={fmt(pausedMs)}
          live={!isWorking}
          color={!isWorking ? "#b45309" : "var(--ink-3)"}
        />
      </div>

      {/* Work Unit + client/project */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "baseline", minWidth: 0 }}>
          <span className="mono" style={{ fontSize: 11, color: "var(--ink-3)", flexShrink: 0 }}>
            {session.workUnitCtrlNo ?? "—"}
          </span>
          <span
            style={{
              fontSize: 12,
              color: "var(--ink-1)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {session.workUnitName ?? ""}
          </span>
        </div>
        {(session.workUnitClientName || session.workUnitProjectName) && (
          <div
            style={{
              fontSize: 11,
              color: "var(--ink-3)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {session.workUnitClientName ?? "—"}
            {session.workUnitProjectName ? ` · ${session.workUnitProjectName}` : ""}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {isWorking ? (
          <button type="button" className="tbl-row-action" onClick={onPause} disabled={busy}>
            Pause
          </button>
        ) : (
          <button type="button" className="tbl-row-action" onClick={onResume} disabled={busy}>
            Resume
          </button>
        )}
        <button type="button" className="tbl-row-action" onClick={onSwitch} disabled={busy}>
          Switch
        </button>
        <button type="button" className="tbl-row-action is-danger" onClick={onStop} disabled={busy}>
          Stop
        </button>
      </div>
    </div>
  );
}

function TimerStat({
  label,
  value,
  live,
  color,
}: {
  label: string;
  value: string;
  live: boolean;
  color: string;
}) {
  return (
    <div
      style={{
        padding: "6px 10px",
        border: "1px solid var(--line)",
        borderRadius: 6,
        background: "var(--bg-page)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <span
        style={{
          fontSize: 10,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          fontWeight: 600,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {live && (
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: color,
              animation: "wmBlink 1s infinite",
            }}
          />
        )}
        {label}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 16,
          fontWeight: 700,
          color,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: 0.5,
        }}
      >
        {value}
      </span>
    </div>
  );
}

function useIsMobile(): boolean {
  const [is, setIs] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  useEffect(() => {
    const onResize = () => setIs(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return is;
}

export function NavbarTimerPill() {
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;
  const role = user?.role;
  // HR is org-level — no time tracking. SA / TA also excluded.
  const enabled =
    role === "admin" || role === "operations" || role === "employee";
  const qc = useQueryClient();
  const { data } = useMeSessionState(enabled);
  const session: SessionRow | null = enabled ? data?.session ?? null : null;

  const [activeMs, setActiveMs] = useState(0);
  const [pausedMs, setPausedMs] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pipOpen, setPipOpen] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const prevSessionStateRef = useRef<string | null>(null);
  const isMobile = useIsMobile();
  const pause = usePauseSession();
  const resume = useResumeSession();
  const stop = useStopSession();
  const switchTo = useSwitchSession();
  const start = useStartSession();
  const heartbeat = useHeartbeat();
  const { confirm, node } = useConfirm();
  const [startPickerOpen, setStartPickerOpen] = useState(false);

  const failureStreak = useRef(0);
  const networkDownTriggered = useRef(false);

  // Sync local ticks to canonical when session changes.
  useEffect(() => {
    if (!session) {
      setActiveMs(0);
      setPausedMs(0);
      return;
    }
    setActiveMs(session.elapsedMs);
    setPausedMs(session.totalPausedMs);
    if (session.state === "working") {
      networkDownTriggered.current = false;
      failureStreak.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specific session fields tracked, not whole object
  }, [session?.id, session?.elapsedMs, session?.totalPausedMs, session?.state]);

  // 1s tick — active counter when working, paused counter when paused.
  useEffect(() => {
    if (!session) return;
    if (session.state === "working") {
      const t = setInterval(() => setActiveMs((v) => v + 1000), 1000);
      return () => clearInterval(t);
    }
    if (session.state === "paused") {
      const t = setInterval(() => setPausedMs((v) => v + 1000), 1000);
      return () => clearInterval(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specific session fields tracked
  }, [session?.id, session?.state]);

  // 30s heartbeat → reconcile active counter; detect network down.
  useEffect(() => {
    if (!session || session.state !== "working") return;
    const t = setInterval(() => {
      heartbeat.mutate(
        { sessionId: session.id, elapsedMs: activeMs },
        {
          onSuccess: (resp) => {
            failureStreak.current = 0;
            setActiveMs(resp.elapsedMs);
          },
          onError: () => {
            failureStreak.current += 1;
            if (
              failureStreak.current >= NETWORK_DOWN_STREAK &&
              !networkDownTriggered.current &&
              navigator.onLine === false
            ) {
              networkDownTriggered.current = true;
              pause.mutate(
                { sessionId: session.id, reason: "network_down" as const },
                {
                  onSuccess: () => toast.warning("Network down — session auto-paused"),
                  onError: () => {
                    networkDownTriggered.current = false;
                  },
                },
              );
            }
          },
        },
      );
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specific session fields tracked
  }, [session?.id, session?.state, activeMs, heartbeat, pause]);

  // online → auto-resume from network_down.
  useEffect(() => {
    const onOnline = () => {
      if (
        session &&
        session.state === "paused" &&
        session.pauseReason === "network_down"
      ) {
        resume.mutate(
          { sessionId: session.id },
          {
            onSuccess: () => {
              toast.success("Network restored — session resumed");
              networkDownTriggered.current = false;
              failureStreak.current = 0;
            },
          },
        );
      }
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specific session fields tracked
  }, [session?.id, session?.state, session?.pauseReason, resume]);

  useChannel(userId ? `user.${userId}` : null, {
    "session.changed": () => {
      qc.invalidateQueries({ queryKey: ["me", "session-state"] });
    },
  });

  useEffect(() => {
    if (!session || session.state === "ended") setPipOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- specific session field tracked
  }, [session?.state]);

  // Auto-open PiP whenever session transitions to "working" (start or resume).
  // Browser requires transient user activation for PiP — the originating click
  // on Start/Resume is usually still within the activation window (~5 s).
  // Skipped silently when PiP unsupported (Firefox, Safari, mobile).
  useEffect(() => {
    const curState = session?.state ?? null;
    const prevState = prevSessionStateRef.current;
    if (
      curState === "working" &&
      prevState !== "working" &&
      PIP_SUPPORTED() &&
      !isMobile &&
      !pipOpen
    ) {
      setPipOpen(true);
    }
    prevSessionStateRef.current = curState;
  }, [session?.state, isMobile, pipOpen]);

  if (!enabled) return null;

  // No active session → render compact "Start session" launcher so the user
  // can spin up a session from anywhere in the app, not just AttendancePage.
  if (!session || session.state === "ended") {
    const onStartPick = async (wu: { workUnitId: string; ctrlNo: string }) => {
      setStartPickerOpen(false);
      const ok = await confirm({
        title: "Start session?",
        message: <>Start working on <strong className="mono">{wu.ctrlNo}</strong>.</>,
        confirmLabel: "Start",
      });
      if (!ok) return;
      start.mutate(
        { workUnitId: wu.workUnitId },
        {
          onSuccess: () => toast.success(`Started ${wu.ctrlNo}`),
          onError: (e: unknown) => toastApiError(e, "Start failed"),
        },
      );
    };
    return (
      <>
        <button
          type="button"
          onClick={() => setStartPickerOpen(true)}
          disabled={start.isPending}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderLeft: "3px solid #059669",
            borderRadius: 6,
            cursor: "pointer",
            color: "#059669",
            fontSize: 12,
            fontWeight: 600,
          }}
          title="Start a work session"
        >
          <span style={{ fontSize: 13, lineHeight: 1 }}>▶</span>
          <span style={{ display: isMobile ? "none" : "inline" }}>Start session</span>
        </button>
        <WorkUnitPickerDialog
          open={startPickerOpen}
          onClose={() => setStartPickerOpen(false)}
          onPick={onStartPick}
          title="Start session — pick a work unit"
        />
        {node}
      </>
    );
  }

  const onPause = async () => {
    const ok = await confirm({
      title: "Pause session?",
      message: "Stop the active timer. You can resume to continue.",
      confirmLabel: "Pause",
    });
    if (!ok) return;
    pause.mutate({ sessionId: session.id, reason: "manual" as const }, {
      onSuccess: () => toast.success("Session paused"),
      onError: (e: unknown) => toastApiError(e, "Pause failed"),
    });
  };

  const onResume = () => {
    resume.mutate({ sessionId: session.id }, {
      onSuccess: () => toast.success("Session resumed"),
      onError: (e: unknown) => toastApiError(e, "Resume failed"),
    });
  };

  const onStop = async () => {
    const ok = await confirm({
      title: "Stop session?",
      message: "End this session. Day stays marked-in until you mark out.",
      confirmLabel: "Stop",
      tone: "danger",
    });
    if (!ok) return;
    stop.mutate({ sessionId: session.id }, {
      onSuccess: () => toast.success("Session stopped"),
      onError: (e: unknown) => toastApiError(e, "Stop failed"),
    });
  };

  const onPick = async (wu: { workUnitId: string; ctrlNo: string }) => {
    setPickerOpen(false);
    const ok = await confirm({
      title: "Switch work unit?",
      message: <>Pause <strong className="mono">{session.workUnitCtrlNo}</strong> and start <strong className="mono">{wu.ctrlNo}</strong>?</>,
      confirmLabel: "Switch",
    });
    if (!ok) return;
    switchTo.mutate({ sessionId: session.id, newWorkUnitId: wu.workUnitId }, {
      onSuccess: () => toast.success(`Switched to ${wu.ctrlNo}`),
      onError: (e: unknown) => toastApiError(e, "Switch failed"),
    });
  };

  const busy =
    pause.isPending || resume.isPending || stop.isPending || switchTo.isPending;

  const isWorking = session.state === "working";
  const accent = isWorking ? "#059669" : "#b45309";

  const openPopover = (e: React.MouseEvent<HTMLElement>) => {
    if (isMobile) {
      setDrawerOpen(true);
      return;
    }
    // Capture currentTarget synchronously — React may null it before setState applies.
    const anchor = e.currentTarget;
    setPopoverAnchor(anchor);
  };
  const closeOverlay = () => {
    setPopoverAnchor(null);
    setDrawerOpen(false);
  };

  return (
    <>
      {/* Inject keyframes used by both pill + PIP fallback */}
      <style>{`@keyframes wmBlink { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>

      {/* Compact navbar pill — hidden when PiP detached */}
      {!pipOpen && (
        <button
          type="button"
          onClick={openPopover}
          className="navbar-timer-pill"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 10px",
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderLeft: `3px solid ${accent}`,
            borderRadius: 6,
            cursor: "pointer",
            font: "inherit",
            color: "var(--ink-1)",
          }}
          title={
            session.workUnitCtrlNo
              ? `${session.workUnitCtrlNo} · ${session.workUnitName ?? ""}`
              : "Session active"
          }
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: accent,
              animation: isWorking ? "wmBlink 1s infinite" : undefined,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              color: accent,
              opacity: 0.85,
              display: isMobile ? "none" : "inline",
            }}
          >
            Work Session
          </span>
          <span
            className="mono tnum"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: accent,
              minWidth: 56,
              display: isMobile ? "none" : "inline",
            }}
          >
            {fmt(isWorking ? activeMs : pausedMs)}
          </span>
          {PIP_SUPPORTED() && !isMobile && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                setPipOpen(true);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: 4,
                background: "var(--surface)",
                border: "1px solid var(--line)",
                color: "var(--ink-3)",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              title="Detach to floating window"
              aria-label="Detach"
            >
              ⤢
            </span>
          )}
        </button>
      )}

      {/* Desktop popover — full controls */}
      <Popover
        open={!!popoverAnchor}
        anchorEl={popoverAnchor}
        onClose={closeOverlay}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div style={{ padding: 0 }}>
          <PillBody
            session={session}
            activeMs={activeMs}
            pausedMs={pausedMs}
            onPause={onPause}
            onResume={onResume}
            onSwitch={() => {
              setPickerOpen(true);
              closeOverlay();
            }}
            onStop={onStop}
            onDetach={() => {
              setPipOpen(true);
              closeOverlay();
            }}
            busy={busy}
          />
        </div>
      </Popover>

      {/* Mobile bottom-sheet — full controls, no detach (PiP unsupported on mobile) */}
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={closeOverlay}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            padding: 1,
            maxHeight: "70vh",
          },
        }}
      >
        <PillBody
          session={session}
          activeMs={activeMs}
          pausedMs={pausedMs}
          onPause={onPause}
          onResume={onResume}
          onSwitch={() => {
            setPickerOpen(true);
            closeOverlay();
          }}
          onStop={onStop}
          onDetach={() => {
            /* PiP unsupported on mobile — button hidden via mobile-detach class */
          }}
          busy={busy}
          hideDetach
        />
      </Drawer>

      {/* Detached PIP window */}
      <PipTimerWindow open={pipOpen} onClose={() => setPipOpen(false)}>
        <PipTimerContent
          session={session}
          activeMs={activeMs}
          pausedMs={pausedMs}
          onPause={onPause}
          onResume={onResume}
          onSwitch={() => setPickerOpen(true)}
          onStop={onStop}
          busy={busy}
        />
      </PipTimerWindow>

      <WorkUnitPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={onPick}
        title="Switch to work unit"
        excludeWorkUnitId={session.workUnitId}
      />
      {node}
    </>
  );
}
